<script setup>
import {
    computed,
    nextTick,
    onMounted,
    reactive,
    ref,
    unref,
    watch,
} from "vue";
import { router, usePage } from "@inertiajs/vue3";
import { useVuelidate } from "@vuelidate/core";
import { url as v$url, helpers } from "@vuelidate/validators";
import { useLocalStorage, useTimeoutFn } from "@vueuse/core";
import BrandModalogShell from "@/Components/Modalogs/BrandModalogShell.vue";
import BrandThrobber from "@/Components/Brand/BrandThrobber.vue";
import { useToastStore } from "@/stores/toast.js";
import Regex from "@/utilities/regex";
import { useModalogStore } from "@/stores/modalog.js";

/**
 * Redesigned "Add a Gift" modalog (GH-848). Single modal with a Copy/Paste a
 * URL | Create a Gift segmented control, replacing the old two-step
 * Create → ConfirmCustom flow for the redesigned pages. Legacy launchers keep
 * using ModalogAddGift.
 */
const props = defineProps({
    listType: {
        type: String,
        default: "wish",
    },
    listUuid: {
        type: String,
        default: null,
    },
});

const modalogStore = useModalogStore();
const { closeModalog } = useModalogStore();
const toastStore = useToastStore();

// --- Destination list; a picker is shown when none was preselected
//     (e.g. the dashboard "Add Gift" button).
const userLists = computed(
    () => usePage().props.auth?.user?.data?.wishlists ?? [],
);
const pickerLists = computed(() => (props.listUuid ? [] : userLists.value));
const selectedListUuid = ref(props.listUuid ?? null);

// --- Tabs
const tab = ref("url");

// --- URL flow
const url = ref("");
const urlInputRef = ref(null);
const urlServerError = ref(null);
const isSaving = ref(false);
const isParsed = ref(false);

const hasShownExtendedConfirmationMessage = useLocalStorage(
    "hasShownExtendedConfirmationMessage",
    false,
    { listenToStorageChanges: true },
);

const timeLeftToClose = ref(hasShownExtendedConfirmationMessage.value ? 1 : 3);

// Whether the success panel shows the full first-time treatment (large icon
// + copy) or the compact repeat flash; snapshotted at save time.
const showFullConfirmation = ref(true);

// The gift parses in the background; after a save the modal closes itself on
// a short countdown. Held at setup scope so a reopen can cancel a pending
// countdown from the previous add.
const { start: startCloseTimer, stop: stopCloseTimer } = useTimeoutFn(
    () => {
        timeLeftToClose.value -= 1;
        if (timeLeftToClose.value > 0) {
            startCloseTimer();
        } else {
            hasShownExtendedConfirmationMessage.value = true;
            closeModalog();
        }
    },
    1000,
    { immediate: false },
);

const rules = {
    url: {
        v$url: helpers.withMessage(
            "Sorry, please check the link and try again.",
            v$url,
        ),
        $lazy: true,
    },
};

const v$ = useVuelidate(rules, { url });

onMounted(() => {
    urlInputRef.value?.focus();
});

const canSubmitUrl = computed(
    () => url.value.trim().length > 3 && !!selectedListUuid.value,
);

async function saveUrl() {
    const isFormValid = await unref(v$).$validate();

    if (!isFormValid || isSaving.value) {
        return;
    }

    isSaving.value = true;
    urlServerError.value = null;

    try {
        await axios.post(route("item.save"), {
            url: url.value,
            list_uuid: selectedListUuid.value,
        });

        // Snapshot the lever before the countdown flips it: the first-ever
        // add gets the full-size panel with copy (3s), repeats get a small
        // text-less flash (1s) — same as the legacy modalog on main.
        showFullConfirmation.value = !hasShownExtendedConfirmationMessage.value;
        isParsed.value = true;
        startCloseTimer();

        toastStore.success("Gift added to your list.");
        router.reload({ only: ["parsing", "items"] });
    } catch (error) {
        if (error?.response?.status === 422) {
            urlServerError.value =
                error.response.data?.url?.[0] ??
                error.response.data?.list_uuid?.[0] ??
                "The URL you entered is invalid. Please try again.";
        } else {
            // Parsing failed outright — steer the user to the custom form
            // with their link carried over.
            custom.url = url.value;
            tab.value = "custom";
            customError.value =
                "We couldn't fetch that link automatically. Complete the fields below to create your gift.";
        }
    } finally {
        isSaving.value = false;
    }
}

// --- Custom ("Create a Gift") flow
const custom = reactive({
    name: "",
    url: "",
    price: "0.00",
    qty_desired: 1,
    notes: "",
    photo_uuid: "",
});

const customError = ref(null);
const customFieldErrors = ref({});

const currencies = computed(() => usePage().props.currencies ?? {});
const currencyCode = ref(
    usePage().props.auth?.user?.data?.preferred_currency ?? "USD",
);

const isValidPrice = computed(
    () => custom.price.toString().search(Regex.priceInputs) >= 0,
);

const isExpertList = computed(() => props.listType === "expert");

const canSubmitCustom = computed(
    () =>
        custom.name.trim().length > 0 &&
        Number(custom.qty_desired) > 0 &&
        isValidPrice.value &&
        !!selectedListUuid.value,
);

// --- Photo upload (direct to Cloudinary on select, confirmed on save)
const photoPreview = ref(null);
const uploading = ref(false);
const uploadError = ref(null);
const fileInput = ref(null);

async function handlePhoto(fileList) {
    const file = fileList?.[0];
    uploadError.value = null;

    if (!file || !file.type.startsWith("image/")) {
        uploadError.value = "Please choose an image file.";
        return;
    }

    uploading.value = true;

    try {
        const { data: config } = await axios.get(
            route("uploads.signature", { photo_type: "custom_gift_photo" }),
        );

        const formData = new FormData();
        formData.append("file", file);
        formData.append("api_key", config.api_key);
        formData.append("signature", config.signature);
        Object.entries(config.options).forEach(([key, value]) =>
            formData.append(key, value),
        );

        const response = await fetch(
            "https://api.cloudinary.com/v1_1/gift-hero/image/upload",
            { method: "POST", body: formData },
        );
        const data = await response.json();

        if (!data.public_id) {
            throw new Error(data.error?.message ?? "Upload failed");
        }

        custom.photo_uuid = data.public_id;
        photoPreview.value = data.secure_url;
    } catch {
        uploadError.value = "Upload failed — please try again.";
    } finally {
        uploading.value = false;
    }
}

async function saveCustom() {
    if (!canSubmitCustom.value || isSaving.value) {
        return;
    }

    isSaving.value = true;
    customError.value = null;
    customFieldErrors.value = {};

    try {
        await axios.post(route("item.store.custom"), {
            ...custom,
            list_uuid: selectedListUuid.value,
            currency_code: currencyCode.value,
        });

        closeModalog();
        toastStore.success("Gift added to your list.");
        router.reload({ only: ["items"] });
    } catch (error) {
        if (error?.response?.status === 422) {
            customFieldErrors.value =
                error.response.data?.errors ?? error.response.data ?? {};
        } else {
            customError.value = "Something went wrong — please try again.";
        }
    } finally {
        isSaving.value = false;
    }
}

const fieldError = (field) => customFieldErrors.value[field]?.[0];

function submit() {
    if (tab.value === "url") {
        saveUrl();
    } else {
        saveCustom();
    }
}

const canSubmit = computed(() =>
    tab.value === "url" ? canSubmitUrl.value : canSubmitCustom.value,
);

const submitLabel = computed(() => {
    if (isSaving.value) {
        return "Saving";
    }

    return tab.value === "url" ? "Next" : "Save Gift";
});

// closeModalog() hides the overlay but leaves this component mounted (stable
// root + hasModalog gate), so state from the last run — including a stale
// "We've got your gift!" success screen — would greet the next open (GH-877).
// Reset to a clean slate every time the modalog re-opens.
watch(
    () => modalogStore.hasModalog,
    (isOpen) => {
        if (isOpen) {
            resetState();
        }
    },
);

function resetState() {
    stopCloseTimer();
    tab.value = "url";
    url.value = "";
    urlServerError.value = null;
    isParsed.value = false;
    isSaving.value = false;
    timeLeftToClose.value = hasShownExtendedConfirmationMessage.value ? 1 : 3;
    unref(v$).$reset();
    Object.assign(custom, {
        name: "",
        url: "",
        price: "0.00",
        qty_desired: 1,
        notes: "",
        photo_uuid: "",
    });
    customError.value = null;
    customFieldErrors.value = {};
    photoPreview.value = null;
    uploadError.value = null;
    selectedListUuid.value = props.listUuid ?? null;
    nextTick(() => urlInputRef.value?.focus());
}

const inputClasses =
    "h-11 w-full rounded-[5px] border border-solid border-[#a79e9b] bg-white px-4 font-brand text-[15px] text-brand-navy placeholder:text-[#3c3938]/50 focus:border-brand-navy focus:outline-none";
const labelClasses = "mb-1.5 block text-[15px] font-bold text-[#3c3938]";
</script>

<template>
    <!-- Stable root; the shell is gated on hasModalog (see BrandModalogShell). -->
    <div>
        <BrandModalogShell v-if="modalogStore.hasModalog" title="Add a Gift">
            <!-- Success state: gift saved, parsing continues in background.
                 First-ever add: full-size icon + copy. Repeats: a compact,
                 text-less flash (the countdown is also shorter). -->
            <div v-if="isParsed" class="flex flex-col items-center py-6">
                <svg
                    :width="showFullConfirmation ? 100 : 75"
                    :height="showFullConfirmation ? 100 : 75"
                    viewBox="0 0 120 120"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <circle cx="60" cy="60" r="60" fill="#E6F7EC" />
                    <circle cx="60" cy="60" r="44" fill="#28A745" />
                    <path
                        d="M42 64 L55 77 L78 46"
                        fill="none"
                        stroke="white"
                        stroke-width="8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="animate-draw-check [stroke-dasharray:60] [stroke-dashoffset:60]"
                    />
                </svg>
                <template v-if="showFullConfirmation">
                    <h3 class="mt-5 text-[20px] font-bold text-brand-navy">
                        We've got your gift!
                    </h3>
                    <p class="mt-2 text-center text-[14px] text-[#3f3f3f]">
                        This window will close automatically and your gift will
                        load in the background.
                    </p>
                </template>
            </div>

            <template v-else>
                <!-- Segmented control -->
                <div
                    class="flex h-14 items-stretch gap-1.5 rounded-[8px] border border-solid border-[#e6dfdd] p-1.5"
                    role="tablist"
                >
                    <button
                        type="button"
                        role="tab"
                        class="flex-1 rounded-[5px] text-[15px] font-bold transition"
                        :class="
                            tab === 'url'
                                ? 'bg-brand-navy text-white'
                                : 'text-brand-navy hover:bg-brand-navy/5'
                        "
                        :aria-selected="tab === 'url'"
                        @click="tab = 'url'"
                    >
                        Copy/Paste a URL
                    </button>
                    <button
                        type="button"
                        role="tab"
                        class="flex-1 rounded-[5px] text-[15px] font-bold transition"
                        :class="
                            tab === 'custom'
                                ? 'bg-brand-navy text-white'
                                : 'text-brand-navy hover:bg-brand-navy/5'
                        "
                        :aria-selected="tab === 'custom'"
                        @click="tab = 'custom'"
                    >
                        Create a Gift
                    </button>
                </div>

                <!-- Destination list picker (dashboard launch) -->
                <div v-if="pickerLists.length" class="mt-6">
                    <label
                        class="mb-1.5 block text-[15px] font-bold text-[#3c3938]"
                        for="add-gift-list"
                        >Add to which list?</label
                    >
                    <div class="relative">
                        <select
                            id="add-gift-list"
                            v-model="selectedListUuid"
                            :class="[inputClasses, 'appearance-none pr-9']"
                        >
                            <option :value="null" disabled>
                                Choose a list…
                            </option>
                            <option
                                v-for="list in pickerLists"
                                :key="list.uuid"
                                :value="list.uuid"
                            >
                                {{ list.name }}
                            </option>
                        </select>
                        <svg
                            viewBox="0 0 13 10"
                            class="pointer-events-none absolute right-4 top-1/2 h-2.5 w-[13px] -translate-y-1/2 text-brand-red"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M6.5 10L0 0h13L6.5 10z" />
                        </svg>
                    </div>
                </div>

                <!-- URL tab -->
                <form
                    v-if="tab === 'url'"
                    class="mt-7"
                    @submit.prevent="saveUrl"
                >
                    <label :class="labelClasses" for="add-gift-url"
                        >Paste a link from anywhere on the web:</label
                    >
                    <input
                        id="add-gift-url"
                        ref="urlInputRef"
                        v-model="url"
                        type="url"
                        placeholder="http://"
                        autocomplete="off"
                        :class="inputClasses"
                    />
                    <p
                        v-for="error of v$.url.$errors"
                        :key="error.$uid"
                        class="mt-2 text-[13px] font-semibold text-brand-red"
                    >
                        {{ error.$message }}
                    </p>
                    <p
                        v-if="urlServerError"
                        class="mt-2 text-[13px] font-semibold text-brand-red"
                    >
                        {{ urlServerError }}
                    </p>

                    <!-- Browser extension callout -->
                    <div
                        class="mt-8 flex items-start gap-3 rounded-[5px] border border-solid border-[#ffa100]/65 p-3.5"
                    >
                        <span
                            class="grid size-5 shrink-0 place-items-center rounded-full bg-[#ffa100] text-[13px] font-bold leading-none text-white"
                            aria-hidden="true"
                            >!</span
                        >
                        <p
                            class="text-[13px] font-semibold leading-[18px] text-[#3f3f3f]"
                        >
                            For a super easy way to add gifts, get the
                            <a
                                :href="route('browser-extensions')"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="underline"
                                >Gift Hero Browser Extension</a
                            >!
                        </p>
                    </div>
                </form>

                <!-- Create a Gift tab -->
                <form v-else class="mt-7" @submit.prevent="saveCustom">
                    <p
                        v-if="customError"
                        class="mb-4 text-[13px] font-semibold text-brand-red"
                    >
                        {{ customError }}
                    </p>

                    <div>
                        <label :class="labelClasses" for="custom-name"
                            >Give Your Gift a Name</label
                        >
                        <input
                            id="custom-name"
                            v-model="custom.name"
                            type="text"
                            placeholder="Tickets to a Music Festival"
                            autocomplete="off"
                            :class="inputClasses"
                        />
                        <p
                            v-if="fieldError('name')"
                            class="mt-2 text-[13px] font-semibold text-brand-red"
                        >
                            {{ fieldError("name") }}
                        </p>
                    </div>

                    <div class="mt-5">
                        <label :class="labelClasses" for="custom-url"
                            >Link
                            <span class="font-normal text-[#3c3938]/60"
                                >(optional)</span
                            ></label
                        >
                        <input
                            id="custom-url"
                            v-model="custom.url"
                            type="text"
                            placeholder="https://"
                            autocomplete="off"
                            :class="inputClasses"
                        />
                        <p
                            v-if="fieldError('url')"
                            class="mt-2 text-[13px] font-semibold text-brand-red"
                        >
                            {{ fieldError("url") }}
                        </p>
                    </div>

                    <div class="mt-5 flex gap-4">
                        <div class="flex-1">
                            <label :class="labelClasses" for="custom-price"
                                >Price</label
                            >
                            <div class="relative">
                                <span
                                    class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[15px] text-[#3c3938]/70"
                                    >{{ currencies[currencyCode] }}</span
                                >
                                <input
                                    id="custom-price"
                                    v-model="custom.price"
                                    type="number"
                                    min="0"
                                    step="any"
                                    :class="[inputClasses, 'pl-9']"
                                    autocomplete="off"
                                />
                            </div>
                        </div>
                        <div class="w-[110px]">
                            <label :class="labelClasses" for="custom-currency"
                                >Currency</label
                            >
                            <select
                                id="custom-currency"
                                v-model="currencyCode"
                                :class="[inputClasses, 'appearance-none px-3']"
                            >
                                <option
                                    v-for="(symbol, code) in currencies"
                                    :key="code"
                                    :value="code"
                                >
                                    {{ code }}
                                </option>
                            </select>
                        </div>
                        <div v-if="!isExpertList" class="w-[80px]">
                            <label :class="labelClasses" for="custom-qty"
                                >Qty</label
                            >
                            <input
                                id="custom-qty"
                                v-model="custom.qty_desired"
                                type="number"
                                min="1"
                                pattern="\d*"
                                :class="inputClasses"
                                autocomplete="off"
                            />
                        </div>
                    </div>
                    <p
                        v-if="!isValidPrice"
                        class="mt-2 text-[13px] font-semibold text-brand-red"
                    >
                        Price can only contain numbers and a single period.
                    </p>

                    <div class="mt-5">
                        <label :class="labelClasses" for="custom-notes"
                            >Gift Details
                            <span class="font-normal text-[#3c3938]/60"
                                >(optional)</span
                            ></label
                        >
                        <textarea
                            id="custom-notes"
                            v-model="custom.notes"
                            rows="2"
                            placeholder="General Admission would be great!"
                            :class="[inputClasses, 'h-auto resize-none py-2.5']"
                        ></textarea>
                    </div>

                    <div class="mt-5">
                        <span :class="labelClasses"
                            >Photo
                            <span class="font-normal text-[#3c3938]/60"
                                >(optional)</span
                            ></span
                        >
                        <div class="flex items-center gap-4">
                            <img
                                v-if="photoPreview"
                                :src="photoPreview"
                                alt=""
                                class="size-16 rounded-[5px] object-cover"
                            />
                            <button
                                type="button"
                                class="rounded-[5px] border border-solid border-[#a79e9b] px-4 py-2.5 text-[14px] font-bold text-brand-navy transition hover:border-brand-navy disabled:opacity-50"
                                :disabled="uploading"
                                @click="fileInput.click()"
                            >
                                {{
                                    uploading
                                        ? "Uploading…"
                                        : photoPreview
                                          ? "Change Photo"
                                          : "Choose a Photo"
                                }}
                            </button>
                            <input
                                ref="fileInput"
                                type="file"
                                accept="image/*"
                                class="hidden"
                                @change="handlePhoto($event.target.files)"
                            />
                        </div>
                        <p
                            v-if="uploadError"
                            class="mt-2 text-[13px] font-semibold text-brand-red"
                        >
                            {{ uploadError }}
                        </p>
                    </div>
                </form>

                <!-- Footer -->
                <div class="mt-9 flex items-center justify-between">
                    <button
                        type="button"
                        class="text-[15px] font-bold text-brand-navy underline"
                        @click="modalogStore.closeModalog()"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="inline-flex h-[52px] min-w-[132px] items-center justify-center gap-2 rounded-brand bg-brand-navy px-6 text-[15px] font-bold text-white transition hover:bg-brand-navy/90 disabled:pointer-events-none"
                        :class="isSaving ? '' : 'disabled:opacity-30'"
                        :disabled="!canSubmit || isSaving || uploading"
                        @click="submit"
                    >
                        {{ submitLabel }}
                        <BrandThrobber v-if="isSaving" />
                    </button>
                </div>
            </template>
        </BrandModalogShell>
    </div>
</template>
